import { createEffect, createSignal, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { type DigestSummary, useDigest } from '../lib/digests';
import { ActionDrawer } from './ActionDrawer';

// Reading drawer for one weekly digest on the shared ActionDrawer surface
// (spec §3.7). The body is the stored email itself (`rendered_html`, fetched on
// open) in a sandboxed iframe — the DB has no structured summary, so we render
// what the recipient actually got. Focus trap / Escape / scroll lock / exit
// animation all come with ActionDrawer.

export type DigestDrawerProps = {
  readonly digest: DigestSummary | null;
  /** Session credential for the detail fetch; defaults to the dev/prod session. */
  readonly token?: string | null;
  readonly onClose: () => void;
};

export function DigestDrawer(props: DigestDrawerProps): JSX.Element {
  // Snapshot survives the `digest → null` close edge so the 200ms exit
  // animation slides out the full content instead of a blanked panel.
  const [last, setLast] = createSignal<DigestSummary | null>(null);
  createEffect(() => {
    if (props.digest) setLast(props.digest);
  });

  // Keyed off the snapshot: the cached body stays rendered through the exit
  // (and on reopen); idle until the first card is opened.
  const detail = useDigest(() => last()?.id ?? null, props.token);

  return (
    <ActionDrawer
      open={props.digest !== null}
      title={t('digests.report_title')}
      onClose={props.onClose}
    >
      <Show when={last()}>
        {(d) => (
          <>
            <p class="digest-drawer__week">
              {t('digests.week_of', { date: d().period_start.slice(0, 10) })}
            </p>
            <Switch>
              <Match when={detail.isLoading}>
                <p class="digest-drawer__status" role="status">
                  {t('digests.loading')}
                </p>
              </Match>
              <Match when={detail.isError}>
                <p class="digest-drawer__status">{t('digests.load_error')}</p>
              </Match>
              <Match when={detail.data}>
                {(dd) => (
                  // The "sanitized frame": an empty `sandbox` blocks scripts,
                  // same-origin, forms and navigation. The HTML is our own MJML
                  // render, but the sandbox neutralises anything unexpected too.
                  // ponytail: links are inert (no allow-top-navigation) — fine for
                  // a reading view; loosen the sandbox if click-through is wanted.
                  <iframe
                    class="digest-drawer__frame"
                    title={t('digests.report_title')}
                    sandbox=""
                    srcdoc={dd().rendered_html}
                  />
                )}
              </Match>
            </Switch>
          </>
        )}
      </Show>
    </ActionDrawer>
  );
}
